/**
 * MUI SpeakerLinkToggle Component
 *
 * This component provides a toggle switch for linking/unlinking wall speakers
 * for symmetrical or independent movement along the room perimeter.
 */

import React from 'react';
import { Box, ToggleButtonGroup, ToggleButton, styled } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ReducersIndex from 'reduxModules/ducks';
import { getAreaProperty } from 'reduxModules/selectors/containerSelectors';
import InputLabel from '../atoms/InputLabel';
import { AnyAction } from '@reduxjs/toolkit';

export interface SpeakerLinkToggleProps {
  show?: boolean;
  disabled?: boolean;
  label?: string;
}

/**
 * Styled container for the toggle switch to match DesignsList ViewControls pattern
 */
const ToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '4px',
  background: theme.palette.mode === 'dark'
    ? theme.palette.grey[700]
    : theme.palette.grey[50],
  borderRadius: '8px',
  padding: '4px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark'
    ? 'inset 0 1px 3px rgba(0, 0, 0, 0.4)'
    : 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
  height: '36px',
  alignItems: 'center',
}));

/**
 * Styled ToggleButton component to match DesignsList ViewButton pattern
 */
const StyledToggleButton = styled(ToggleButton)<{ checked: boolean }>(({ theme, checked }) => ({
  height: '28px',
  padding: '0 12px',
  borderRadius: theme.shape.borderRadius,
  border: 'none',
  background: checked
    ? theme.palette.background.default
    : 'transparent',
  overflow: 'hidden',
  color: checked ? theme.palette.text.primary : theme.palette.text.secondary,
  textOverflow: 'ellipsis',
  fontFamily: '"Open Sans"',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: checked ? 600 : 500,
  lineHeight: '150%',
  letterSpacing: '-0.28px',
  fontFeatureSettings: "'liga' off, 'clig' off",
  textTransform: 'none',
  minWidth: 'auto',
  boxShadow: checked
    ? theme.palette.mode === 'dark'
      ? '0px 1px 3px rgba(0, 0, 0, 0.3)'
      : '0px 1px 2px rgba(0, 0, 0, 0.1)'
    : 'none',
  transition: 'all 0.2s ease-in-out',
  '&.Mui-selected': {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    fontWeight: 600,
    boxShadow: theme.palette.mode === 'dark'
      ? '0px 1px 3px rgba(0, 0, 0, 0.3)'
      : '0px 1px 2px rgba(0, 0, 0, 0.1)',
  },
  '&:hover': {
    backgroundColor: checked
      ? theme.palette.background.default
      : theme.palette.action.hover,
    color: checked ? theme.palette.text.primary : theme.palette.text.primary,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
}));

/**
 * MUI SpeakerLinkToggle component
 *
 * @param props - Component props
 * @returns The MUI SpeakerLinkToggle component
 */
const SpeakerLinkToggle: React.FC<SpeakerLinkToggleProps> = ({ show = true, disabled = false, label }) => {
  const dispatch = useDispatch();

  // Get current linked state from Redux
  const linkedSpeakers = useSelector((state: any) =>
    getAreaProperty(state, 'speakers.linkedSpeakers')
  ) as boolean;

  /**
   * Handle link toggle change
   * Only dispatch toggleSpeakerLink when the clicked button differs from current state
   *
   * @param event - The change event
   * @param newValue - The new link state value (true = linked, false = unlinked)
   */
  const handleLinkChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: boolean | null
  ): void => {
    // Prevent deselection (null value) and only dispatch if state actually changes
    if (newValue !== null && newValue !== linkedSpeakers) {
      dispatch(
        (ReducersIndex.container as any).toggleSpeakerLink() as unknown as AnyAction
      );
    }
  };

  if (!show) {
    return null;
  }

  return (
    <Box className="d-flex flex-column" sx={{ width: '100%' }}>
      {label && <InputLabel>{label}</InputLabel>}
      <ToggleContainer>
        <ToggleButtonGroup
          value={linkedSpeakers}
          exclusive
          onChange={handleLinkChange}
          aria-label="speaker link toggle"
          disabled={disabled}
          sx={{
            width: '100%',
            '& .MuiToggleButtonGroup-grouped': {
              border: 'none',
              flex: 1,
            }
          }}
        >
          <StyledToggleButton
            value={true}
            aria-label="linked"
            checked={linkedSpeakers === true}
          >
            Linked
          </StyledToggleButton>
          <StyledToggleButton
            value={false}
            aria-label="unlink"
            checked={linkedSpeakers === false}
          >
            Unlink
          </StyledToggleButton>
        </ToggleButtonGroup>
      </ToggleContainer>
    </Box>
  );
};

export default SpeakerLinkToggle;
